import { useState, useEffect, useContext } from "react";
import axios from "axios";
import PageTitle from "../PageTitle";
import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/system";
import EnhancedTable from "../../layouts/table";
import {
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import FilterForm from "./FilterForm";
import CategoryModal from "./CategoryModal";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import LoadingTableRows from "../LoadingTableRows";
import { AppContext } from "../../scripts/context/App";
import ConfirmationModal from "../ConfirmationModal";

export interface IFilter {
  status: boolean;
  data: {
    name: string;
    notes: string;
  };
}

export const initFilterData: IFilter = {
  status: true,
  data: {
    name: "",
    notes: "",
  },
};

interface INewCategories {
  status: boolean;
  edit: boolean;
  data: {
    id: number;
    name: string;
    notes: string;
    status: string;
  };
}
const initCategoriesState: INewCategories = {
  status: false,
  edit: false,
  data: {
    id: 0,
    name: "",
    notes: "",
    status: "",
  },
};

export default function TicketCategories() {
  const { user } = useContext(AppContext);
  const [tableData, setTableDatas] = useState({
    loading: true,
    page: 1,
    pagesCount: -1,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    tableHead: [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "note",
        numeric: false,
        disablePadding: false,
        label: "Note",
      },
      {
        id: "status",
        numeric: false,
        disablePadding: false,
        label: "Status",
      },
      {
        id: "actions",
        numeric: false,
        disablePadding: false,
        label: "Actions",
      },
    ],
    tableItems: [],
  });
  const setTableData = (data: any) => setTableDatas({ ...tableData, ...data });

  const [filter, setFilter] = useState<IFilter>(initFilterData);
  const [categories, setCategories] =
    useState<INewCategories>(initCategoriesState);
  const [sort, setSort] = useState({
    sortBy: "",
    sortOrder: "",
  });
  const [confirmDelete, setConfirmDelete] = useState({
    loading: false,
    status: false,
    id: "",
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTableData({
      rowsPerPage: Number(event.target.value),
      page: 1,
    });

    getCategories({
      name: filter.data.name,
      notes: filter.data.notes,
      ...sort,
      page: 1,
      perPage: event.target.value,
    });
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    getCategories({
      name: filter.data.name,
      notes: filter.data.notes,
      ...sort,
      page: newPage,
      perPage: tableData.rowsPerPage,
    });

    setTableData({ page: newPage });
  };

  const changeStatus = debounce((category_id) => {
    if (!user.permissions.includes("active_inactive_support_category"))
      return toast.error("You dont have permissions to do this action");

    axios.post("/support-categories/change-status", { category_id });
  }, 800);

  const getCategories = async (params?: any) => {
    if (!user.permissions.includes("view_support_category")) return;

    try {
      const {
        data: { data },
      } = await axios.get("/support-categories/get-categories", { params });

      setTableData({
        loading: false,
        tableItems: data.data,
        pagesCount: data.total,
        rowsPerPage: data.per_page,
        page: data.current_page,
      });
    } catch (error: any) {
      setTableData({ loading: false });

      toast.error(
        error.response.data.message ||
        "Something went wrong. Please try again later or contact support."
      );
      console.log(error);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  const deleteCategory = async (id: number | string) => {
    if (!user.permissions.includes("delete_support_category"))
      return toast.error("You dont have permissions to do this action");

    setConfirmDelete({ ...confirmDelete, loading: true });
    try {
      await axios.delete("/support-categories/delete", {
        data: { category_id: id },
      });

      toast.success("Category deleted successfully");
      setTableData({
        tableItems: tableData.tableItems.filter((item: any) => item.id !== id),
      });
      setConfirmDelete({ loading: false, status: false, id: "" });
    } catch (error: any) {
      console.log("error while delete categories: ", error);

      toast.error(
        error.response?.data?.message ||
        "Something went wrong. Please try again later or contact support."
      );
      setConfirmDelete({ loading: false, status: false, id: "" });
    }
  };

  if (!user.permissions.includes("view_support_category"))
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <h3>You dont have permissions to view this page</h3>
      </Box>
    );

  return (
    <>
      <PageTitle
        title="Ticket Categories Management"
        description="Manage your ticket categories"
        actionLabel="Add new category"
        action={() => {
          if (!user.permissions.includes("add_support_category"))
            return toast.error("You dont have permissions to do this action");

          setCategories({ ...categories, status: true, edit: false });
        }}
        startIcon={<AddIcon />}
      />
      <CategoryModal
        data={categories}
        getCategories={() => {
          getCategories({
            name: filter.data.name,
            notes: filter.data.notes,
            ...sort,
            page: tableData.page,
            perPage: tableData.rowsPerPage,
          });
        }}
        setData={(object: any) =>
          setCategories({
            ...categories,
            data: { ...categories.data, ...object },
          })
        }
        handleClose={() => {
          setCategories(initCategoriesState);
        }}
      />
      <ConfirmationModal
        open={confirmDelete.status}
        onClose={() => setConfirmDelete({ ...confirmDelete, status: false })}
        onConfirm={() => deleteCategory(confirmDelete.id)}
        title="Are you sure?"
        message={"This action cannot be undone, please confirm to continue"}
        loading={confirmDelete.loading}
      />

      <Box sx={{ p: 3 }}>
        <EnhancedTable
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          handleRequestSortHeader={(sortOrder, sortBy) => {
            if (!sortBy) return;

            getCategories({
              name: filter.data.name,
              notes: filter.data.notes,
              sortBy,
              sortOrder,
              perPage: tableData.rowsPerPage,
              page: 1,
            });

            setSort({ sortBy, sortOrder });
          }}
          minTableHeadWidth="150px"
          TableRows={
            tableData.loading ? (
              <LoadingTableRows elementsCount={tableData.tableHead.length} />
            ) : (
              tableData.tableItems.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      maxWidth: "200px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.notes}
                  </TableCell>
                  <TableCell>
                    <FormGroup
                      sx={{ maxWidth: "fit-content", justifyContent: "center" }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              typeof row.status === "string"
                                ? row.status.toLowerCase() === "active"
                                : row.status
                            }
                          />
                        }
                        label={
                          typeof row.status === "string"
                            ? row.status.toLowerCase() === "active"
                              ? "Active"
                              : "Inactive"
                            : row.status
                              ? "Active"
                              : "Inactive"
                        }
                        onChange={() => {
                          changeStatus(row.id);

                          const tableItems = tableData.tableItems
                            .map((item: any) => {
                              if (item.id === row.id) {
                                item.status =
                                  item.status.toLowerCase() === "active"
                                    ? "Inactive"
                                    : "Active";
                              }
                              return item;
                            })
                            .filter((item) => item.status);

                          setTableData({ tableItems });
                        }}
                      />
                    </FormGroup>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit category">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          if (
                            !user.permissions.includes("edit_support_category")
                          )
                            return toast.error(
                              "You dont have permissions to do this action"
                            );

                          setCategories({
                            status: true,
                            edit: true,
                            data: row,
                          });
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete category">
                      <IconButton
                        color="error"
                        onClick={() =>
                          setConfirmDelete({ loading: false, id: row.id, status: true })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )
          }
          {...tableData}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() =>
                  setFilter({
                    ...filter,
                    status: !filter.status,
                  })
                }
              >
                {filter.status ? <FilterListIcon /> : <FilterListOffIcon />}
              </IconButton>
              <Typography variant="h6">Filter by</Typography>
            </Box>
            {filter.status && (
              <FilterForm
                filter={filter}
                setFilter={setFilter}
                handleSearch={(data) => {
                  if (data?.data) {
                    getCategories({
                      name: data.data.name,
                      notes: data.data.notes,

                      sortBy: sort.sortBy,
                      sort: sort.sortOrder,
                    });
                  } else {
                    getCategories({
                      name: filter.data.name,
                      notes: filter.data.notes,
                      sortBy: sort.sortBy,
                      sort: sort.sortOrder,
                    });
                  }
                }}
              />
            )}
          </Box>
        </EnhancedTable>
      </Box>
    </>
  );
}
